import React, { useEffect, useState, useCallback } from "react";
import { Typography, Alert, Tabs } from "antd";
import { connect } from "react-redux";
import dayjs from "dayjs";
import {
  fetchOverview,
  fetchVideoStats,
  fetchTopVideos,
  fetchVideoTimeline,
  fetchCommentStats,
  fetchTopCommenters,
  fetchReactionStats,
  fetchTopReactedVideos,
  fetchPostStats,
  fetchBadgeStats,
  clearAnalytics,
} from "../../store/actions/analyticsActions";
import {
  fetchQoEOverview,
  fetchQoEByDevice,
  fetchQoEByPlan,
  fetchQoEByCategory,
  fetchQoEByDifficulty,
  fetchQoETimeseries,
  fetchTrailQoE,
  fetchTrailEngagement,
  fetchTrailFunnel,
  fetchModuleQoE,
  fetchModuleDropOff,
  fetchVideoQoE,
  fetchVideoEngagement,
  fetchConcurrentViewers,
  fetchStartupHealth,
} from "../../store/actions/muxAnalyticsActions";
import { fetchTracks } from "../../store/actions/trackActions";
import { fetchModulesForSelector } from "../../store/actions/moduleActions";
import DateRangeFilter from "./DateRangeFilter";
import OverviewCards from "./OverviewCards";
import VideoSection from "./VideoSection";
import EngagementSection from "./EngagementSection";
import BadgeSection from "./BadgeSection";
import QoESection from "./QoESection";
import MonitoringSection from "./MonitoringSection";

const { Title } = Typography;

const Analytics = ({
  overview,
  videoStats,
  topVideos,
  videoTimeline,
  commentStats,
  topCommenters,
  reactionStats,
  topReactedVideos,
  postStats,
  badgeStats,
  // Mux state
  qoeOverview,
  qoeByDevice,
  qoeByPlan,
  qoeByCategory,
  qoeByDifficulty,
  qoeTimeseries,
  trailQoe,
  trailEngagement,
  trailFunnel,
  moduleQoe,
  moduleDropOff,
  videoQoe,
  videoEngagement,
  concurrentViewers,
  startupHealth,
  // Selectors
  tracks,
  modules,
  loading,
  error,
  muxError,
  // Original actions
  fetchOverview,
  fetchVideoStats,
  fetchTopVideos,
  fetchVideoTimeline,
  fetchCommentStats,
  fetchTopCommenters,
  fetchReactionStats,
  fetchTopReactedVideos,
  fetchPostStats,
  fetchBadgeStats,
  clearAnalytics,
  // Mux actions
  fetchQoEOverview,
  fetchQoEByDevice,
  fetchQoEByPlan,
  fetchQoEByCategory,
  fetchQoEByDifficulty,
  fetchQoETimeseries,
  fetchTrailQoE,
  fetchTrailEngagement,
  fetchTrailFunnel,
  fetchModuleQoE,
  fetchModuleDropOff,
  fetchVideoQoE,
  fetchVideoEngagement,
  fetchConcurrentViewers,
  fetchStartupHealth,
  // Selector actions
  fetchTracks,
  fetchModulesForSelector,
}) => {
  const defaultStartDate = dayjs().subtract(1, "year").format("YYYY-MM-DD");
  const defaultEndDate = dayjs().format("YYYY-MM-DD");

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [groupBy, setGroupBy] = useState("day");
  const [activeTab, setActiveTab] = useState("overview");

  const loadAllData = useCallback(() => {
    fetchOverview(startDate, endDate);
    fetchVideoStats(startDate, endDate);
    fetchTopVideos(startDate, endDate, 10);
    fetchVideoTimeline(startDate, endDate, groupBy);
    fetchCommentStats(startDate, endDate);
    fetchTopCommenters(startDate, endDate, 10);
    fetchReactionStats(startDate, endDate);
    fetchTopReactedVideos(startDate, endDate, 10);
    fetchPostStats(startDate, endDate);
    fetchBadgeStats(startDate, endDate);
  }, [
    startDate, endDate, groupBy,
    fetchOverview, fetchVideoStats, fetchTopVideos, fetchVideoTimeline,
    fetchCommentStats, fetchTopCommenters, fetchReactionStats,
    fetchTopReactedVideos, fetchPostStats, fetchBadgeStats,
  ]);

  useEffect(() => {
    loadAllData();
    return () => {
      clearAnalytics();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleSearch = () => {
    loadAllData();
  };

  const handleClear = () => {
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setGroupBy("day");
    fetchOverview(defaultStartDate, defaultEndDate);
    fetchVideoStats(defaultStartDate, defaultEndDate);
    fetchTopVideos(defaultStartDate, defaultEndDate, 10);
    fetchVideoTimeline(defaultStartDate, defaultEndDate, "day");
    fetchCommentStats(defaultStartDate, defaultEndDate);
    fetchTopCommenters(defaultStartDate, defaultEndDate, 10);
    fetchReactionStats(defaultStartDate, defaultEndDate);
    fetchTopReactedVideos(defaultStartDate, defaultEndDate, 10);
    fetchPostStats(defaultStartDate, defaultEndDate);
    fetchBadgeStats(defaultStartDate, defaultEndDate);
  };

  const isAnyLoading = Object.values(loading).some(Boolean);

  const tabItems = [
    {
      key: "overview",
      label: "Visao Geral",
      children: (
        <div>
          <div style={{ marginBottom: 24 }}>
            <OverviewCards overview={overview} loading={loading.overview} />
          </div>
          <VideoSection
            videoStats={videoStats}
            topVideos={topVideos}
            timeline={videoTimeline}
            loading={loading}
          />
        </div>
      ),
    },
    {
      key: "engagement",
      label: "Engajamento",
      children: (
        <EngagementSection
          commentStats={commentStats}
          topCommenters={topCommenters}
          reactionStats={reactionStats}
          topReactedVideos={topReactedVideos}
          postStats={postStats}
          loading={loading}
        />
      ),
    },
    {
      key: "badges",
      label: "Badges",
      children: <BadgeSection badgeStats={badgeStats} loading={loading.badges} />,
    },
    {
      key: "qoe",
      label: "QoE Streaming",
      destroyInactiveTabPane: true,
      children: (
        <QoESection
          muxError={muxError}
          qoeOverview={qoeOverview}
          qoeByDevice={qoeByDevice}
          qoeByPlan={qoeByPlan}
          qoeByCategory={qoeByCategory}
          qoeByDifficulty={qoeByDifficulty}
          qoeTimeseries={qoeTimeseries}
          trailQoe={trailQoe}
          trailEngagement={trailEngagement}
          trailFunnel={trailFunnel}
          moduleQoe={moduleQoe}
          moduleDropOff={moduleDropOff}
          videoQoe={videoQoe}
          videoEngagement={videoEngagement}
          topVideos={topVideos}
          tracks={tracks}
          modules={modules}
          loading={loading}
          fetchQoEOverview={fetchQoEOverview}
          fetchQoEByDevice={fetchQoEByDevice}
          fetchQoEByPlan={fetchQoEByPlan}
          fetchQoEByCategory={fetchQoEByCategory}
          fetchQoEByDifficulty={fetchQoEByDifficulty}
          fetchQoETimeseries={fetchQoETimeseries}
          fetchTrailQoE={fetchTrailQoE}
          fetchTrailEngagement={fetchTrailEngagement}
          fetchTrailFunnel={fetchTrailFunnel}
          fetchModuleQoE={fetchModuleQoE}
          fetchModuleDropOff={fetchModuleDropOff}
          fetchVideoQoE={fetchVideoQoE}
          fetchVideoEngagement={fetchVideoEngagement}
          fetchTracks={fetchTracks}
          fetchModulesForSelector={fetchModulesForSelector}
        />
      ),
    },
    {
      key: "monitoring",
      label: "Monitoramento",
      destroyInactiveTabPane: true,
      children: (
        <MonitoringSection
          muxError={muxError}
          concurrentViewers={concurrentViewers}
          startupHealth={startupHealth}
          tracks={tracks}
          loading={loading}
          fetchConcurrentViewers={fetchConcurrentViewers}
          fetchStartupHealth={fetchStartupHealth}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "0" }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        Dashboard Analytics
      </Title>

      {error && (
        <Alert
          message="Erro"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        groupBy={groupBy}
        onDateChange={handleDateChange}
        onGroupByChange={setGroupBy}
        onSearch={handleSearch}
        onClear={handleClear}
        loading={isAnyLoading}
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  overview: state.analytics.overview,
  videoStats: state.analytics.videoStats,
  topVideos: state.analytics.topVideos,
  videoTimeline: state.analytics.videoTimeline,
  commentStats: state.analytics.commentStats,
  topCommenters: state.analytics.topCommenters,
  reactionStats: state.analytics.reactionStats,
  topReactedVideos: state.analytics.topReactedVideos,
  postStats: state.analytics.postStats,
  badgeStats: state.analytics.badgeStats,
  // Mux state
  qoeOverview: state.analytics.qoeOverview,
  qoeByDevice: state.analytics.qoeByDevice,
  qoeByPlan: state.analytics.qoeByPlan,
  qoeByCategory: state.analytics.qoeByCategory,
  qoeByDifficulty: state.analytics.qoeByDifficulty,
  qoeTimeseries: state.analytics.qoeTimeseries,
  trailQoe: state.analytics.trailQoe,
  trailEngagement: state.analytics.trailEngagement,
  trailFunnel: state.analytics.trailFunnel,
  moduleQoe: state.analytics.moduleQoe,
  moduleDropOff: state.analytics.moduleDropOff,
  videoQoe: state.analytics.videoQoe,
  videoEngagement: state.analytics.videoEngagement,
  concurrentViewers: state.analytics.concurrentViewers,
  startupHealth: state.analytics.startupHealth,
  // Selectors
  tracks: state.tracks.tracks,
  modules: state.modules.selectorModules,
  loading: state.analytics.loading,
  error: state.analytics.error,
  muxError: state.analytics.muxError,
});

export default connect(mapStateToProps, {
  fetchOverview,
  fetchVideoStats,
  fetchTopVideos,
  fetchVideoTimeline,
  fetchCommentStats,
  fetchTopCommenters,
  fetchReactionStats,
  fetchTopReactedVideos,
  fetchPostStats,
  fetchBadgeStats,
  clearAnalytics,
  // Mux actions
  fetchQoEOverview,
  fetchQoEByDevice,
  fetchQoEByPlan,
  fetchQoEByCategory,
  fetchQoEByDifficulty,
  fetchQoETimeseries,
  fetchTrailQoE,
  fetchTrailEngagement,
  fetchTrailFunnel,
  fetchModuleQoE,
  fetchModuleDropOff,
  fetchVideoQoE,
  fetchVideoEngagement,
  fetchConcurrentViewers,
  fetchStartupHealth,
  // Selector actions
  fetchTracks,
  fetchModulesForSelector,
})(Analytics);
